<section class="plan-change" aria-live="polite">
  <!-- Loading -->
  <p class="plan-change__loading" *ngIf="state === 'loading'">Loading plans…</p>

  <!-- Processing: the body is the spinner, and the footer is hidden. -->
  <div class="plan-change__processing" *ngIf="state === 'processing'" role="status">
    <span class="plan-change__spinner" aria-hidden="true"></span>
    <p class="plan-change__processing-title">{{ processingTitle }}</p>
    <p class="plan-change__processing-sub">
      This can take a few moments. Please don't close this window.
    </p>
  </div>

  <!-- Not eligible / a change is already scheduled -->
  <ng-container *ngIf="state === 'ineligible'">
    <div class="plan-change__pending" *ngIf="pendingChange as pending; else notEligible">
      <span class="plan-change__chip">Upcoming</span>
      <div class="plan-change__pending-copy">
        <p class="plan-change__pending-title">A plan change is already scheduled</p>
        <p class="plan-change__pending-sub">
          It takes effect on {{ formatDate(pending.effective_at) }}. Cancel it to schedule a
          different one.
        </p>
      </div>
      <button
        type="button"
        class="plan-change__link"
        [disabled]="isCancelling"
        (click)="cancelPending()"
      >
        {{ isCancelling ? 'Cancelling…' : 'Cancel scheduled change' }}
      </button>
    </div>
    <ng-template #notEligible>
      <div class="plan-change__alert" role="alert">{{ ineligibleCopy() }}</div>
    </ng-template>
    <p class="plan-change__error" *ngIf="errorMessage && pendingChange" role="alert">
      {{ errorMessage }}
    </p>
  </ng-container>

  <!-- Select -->
  <ng-container *ngIf="state === 'select' && current">
    <div class="plan-change__current">
      <div>
        <p class="plan-change__eyebrow">Current plan</p>
        <p class="plan-change__current-name">{{ current.name }}</p>
        <p class="plan-change__current-sub">Renews {{ renewalLabel }}</p>
      </div>
      <p class="plan-change__current-price">
        <s class="plan-change__list-price" *ngIf="listPriceLabel(current) as list">{{ list }}</s>
        {{ money(current.monthly_price) }}<span>/mo</span>
      </p>
    </div>

    <section class="plan-change__group" *ngIf="upgrades.length">
      <h3 class="plan-change__group-title">Upgrade</h3>
      <div class="plan-change__tiles">
        <button
          type="button"
          class="plan-change__tile"
          *ngFor="let option of upgrades; let i = index"
          [class.plan-change__tile--selected]="isSelected(option)"
          [class.plan-change__tile--light]="tileIsLight(option, i)"
          [style.background]="tileBackground(option, i)"
          [attr.aria-pressed]="isSelected(option)"
          (click)="selectPlan(option)"
        >
          <span class="plan-change__tile-check" *ngIf="isSelected(option)" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="currentColor" />
              <path
                d="M6 10.5L8.75 13L14 7.5"
                stroke="#ffffff"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="plan-change__tile-name" [title]="option.name">{{ option.name }}</span>
          <span class="plan-change__tile-washes">{{ washesLabel(option) }}</span>
          <span class="plan-change__tile-price">
            <s class="plan-change__list-price" *ngIf="listPriceLabel(option) as list">{{ list }}</s>
            {{ money(option.monthly_price) }}
          </span>
        </button>
      </div>
    </section>

    <section class="plan-change__group" *ngIf="downgrades.length">
      <h3 class="plan-change__group-title">Downgrade</h3>
      <div class="plan-change__tiles">
        <button
          type="button"
          class="plan-change__tile"
          *ngFor="let option of downgrades; let i = index"
          [class.plan-change__tile--selected]="isSelected(option)"
          [class.plan-change__tile--light]="tileIsLight(option, i + upgrades.length)"
          [style.background]="tileBackground(option, i + upgrades.length)"
          [attr.aria-pressed]="isSelected(option)"
          (click)="selectPlan(option)"
        >
          <span class="plan-change__tile-check" *ngIf="isSelected(option)" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="10" fill="currentColor" />
              <path
                d="M6 10.5L8.75 13L14 7.5"
                stroke="#ffffff"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="plan-change__tile-name" [title]="option.name">{{ option.name }}</span>
          <span class="plan-change__tile-washes">{{ washesLabel(option) }}</span>
          <span class="plan-change__tile-price">
            <s class="plan-change__list-price" *ngIf="listPriceLabel(option) as list">{{ list }}</s>
            {{ money(option.monthly_price) }}
          </span>
        </button>
      </div>
    </section>

    <p class="plan-change__empty" *ngIf="!upgrades.length && !downgrades.length">
      No other monthly plans are sold at this location.
    </p>

    <ng-container *ngIf="selected">
      <section class="plan-change__group">
        <h3 class="plan-change__group-title">When should the change apply?</h3>
        <div class="plan-change__timing">
          <button
            type="button"
            class="plan-change__option plan-change__option--boxed"
            [class.plan-change__option--selected]="timing === 'immediate'"
            [disabled]="!immediateAllowed"
            (click)="selectTiming('immediate')"
          >
            <span
              class="plan-change__radio"
              [class.plan-change__radio--checked]="timing === 'immediate'"
            ></span>
            <span class="plan-change__option-copy">
              <span class="plan-change__option-title">Immediately</span>
              <span class="plan-change__option-sub">{{ immediateSubLabel }}</span>
            </span>
          </button>
          <button
            type="button"
            class="plan-change__option plan-change__option--boxed"
            [class.plan-change__option--selected]="timing === 'next_cycle'"
            (click)="selectTiming('next_cycle')"
          >
            <span
              class="plan-change__radio"
              [class.plan-change__radio--checked]="timing === 'next_cycle'"
            ></span>
            <span class="plan-change__option-copy">
              <span class="plan-change__option-title">From next cycle</span>
              <span class="plan-change__option-sub">Starts on renewal, {{ renewalLabel }}</span>
            </span>
          </button>
        </div>
      </section>

      <section class="plan-change__group" *ngIf="showRefund">
        <h3 class="plan-change__group-title">
          Refund
          <span class="plan-change__group-sub">{{ refundDestinationLabel }}</span>
        </h3>
        <div class="plan-change__refunds" role="radiogroup" aria-label="Refund">
          <button
            type="button"
            class="plan-change__refund-row"
            *ngFor="let choice of refundChoices"
            role="radio"
            [attr.aria-checked]="refundOption === choice.option"
            [class.plan-change__refund-row--selected]="refundOption === choice.option"
            (click)="selectRefund(choice.option)"
          >
            <span
              class="plan-change__radio"
              [class.plan-change__radio--checked]="refundOption === choice.option"
            ></span>
            <span class="plan-change__option-copy">
              <span class="plan-change__option-title">{{ refundChoiceLabel(choice.option) }}</span>
              <span
                class="plan-change__option-sub"
                *ngIf="choice.option === 'prorated' && refundOption === 'prorated'"
              >
                {{ refundChoiceSub(choice.option) }}
              </span>
            </span>
            <span class="plan-change__refund-value">{{ refundChoiceValue(choice) }}</span>
          </button>
        </div>
        <div class="plan-change__amount" *ngIf="refundOption === 'custom'">
          <span class="plan-change__amount-prefix">$</span>
          <input
            class="plan-change__amount-input"
            type="text"
            inputmode="decimal"
            placeholder="0.00"
            aria-label="Custom refund amount"
            [value]="customRefundDraft"
            (input)="onCustomRefundInput($any($event.target).value)"
          />
          <span
            class="plan-change__amount-hint"
            [class.plan-change__amount-hint--error]="
              customRefundDraft && customRefundValue === null
            "
          >
            Max {{ money(customRefundMax) }}
          </span>
        </div>
      </section>

      <section class="plan-change__group" *ngIf="showWashAllowance && selected.immediate">
        <h3 class="plan-change__group-title">Washes for the rest of this cycle</h3>
        <div class="plan-change__allowance">
          <label class="plan-change__allowance-field">
            <span>Washes left this cycle</span>
            <input
              class="plan-change__allowance-input"
              type="text"
              inputmode="numeric"
              aria-label="Washes left this cycle"
              [value]="washOverrideDraft"
              (input)="onWashOverrideInput($any($event.target).value)"
            />
          </label>
          <p
            class="plan-change__allowance-hint"
            [class.plan-change__amount-hint--error]="washOverrideInvalid"
          >
            Default {{ selected.immediate.wash_allowance.washes_this_cycle_default }} · up to
            {{ washOverrideMax }}. Every cycle from {{ renewalLabel }}:
            {{ selected.immediate.wash_allowance.washes_per_renewal }}
          </p>
        </div>
      </section>

      <section class="plan-change__summary" *ngIf="summaryRows.length">
        <p class="plan-change__eyebrow">Summary</p>
        <div class="plan-change__summary-row" *ngFor="let row of summaryRows">
          <div class="plan-change__summary-copy">
            <p class="plan-change__summary-label">{{ row.label }}</p>
            <p class="plan-change__summary-plan" *ngIf="row.plan">
              Updated Plan: <strong>{{ row.plan }}</strong>
            </p>
            <p class="plan-change__summary-sub" *ngIf="row.sub">{{ row.sub }}</p>
          </div>
          <div class="plan-change__summary-values">
            <p
              class="plan-change__summary-value"
              [class.plan-change__summary-value--big]="row.emphasis"
            >
              {{ row.value }}
            </p>
            <p class="plan-change__summary-sub" *ngIf="row.valueSub">{{ row.valueSub }}</p>
          </div>
        </div>
      </section>
    </ng-container>

    <p class="plan-change__error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
  </ng-container>

  <footer class="plan-change__footer" *ngIf="state !== 'processing'">
    <button
      type="button"
      class="plan-change__back"
      *ngIf="showBackButton; else backSpacer"
      (click)="onBack()"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M10 3L5 8L10 13"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Back
    </button>
    <!-- Keeps the actions right-aligned when Back is suppressed. -->
    <ng-template #backSpacer><span></span></ng-template>
    <div class="plan-change__footer-actions" *ngIf="state === 'select'">
      <button type="button" class="plan-change__secondary" (click)="onBack()">Cancel</button>
      <button
        type="button"
        class="plan-change__primary"
        [disabled]="!canConfirm"
        (click)="confirm()"
      >
        {{ ctaLabel }}
      </button>
    </div>
  </footer>
</section>
